// WIP Types

import { EdgeInstanceType, isEdge, isNonNaNNumber, Milliseconds, UTCPeriodHrMins, ValidBusinessObject, ValidBusinessObjectList } from '@iotv/iotv-v3-types'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../../../util/lambda'
import config from '@iotv/config'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

export type RollingPatternConfig = {
  duration: number // (minutes)
  delay: number // (minutes)
  startTimeUtcSeconds: number //(UTC seconds)
  moistureThreshold: number // (0-100)
}

export type RollingPatternDisabledConfig = {
  duration: 0 // (minutes)
  delay: 0 // (minutes)
  startTimeUtcSeconds: number //(UTC seconds)
  moistureThreshold: number // (0-100)
}

export const rollingPatternConfigToStartStopDates = (rpc: RollingPatternConfig) => {
  const { duration, delay, startTimeUtcSeconds } = rpc
  const startPatternUtc = new Date(startTimeUtcSeconds * Milliseconds.SECOND)
  const startOperationUtc = new Date(startPatternUtc)
  startOperationUtc.setMinutes(startOperationUtc.getUTCMinutes() + delay)
  const stopOperationUtc = new Date(startOperationUtc)
  stopOperationUtc.setMinutes(stopOperationUtc.getUTCMinutes() + duration)

  return {
    startPatternUtc,
    startOperationUtc,
    stopOperationUtc,
  }
}

export const displayRollingPatternStartStopDate = (rpc: RollingPatternConfig) => {
  const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }
  const { startPatternUtc, startOperationUtc, stopOperationUtc } = rollingPatternConfigToStartStopDates(rpc)
  return {
    startPattern: startPatternUtc.toLocaleString(config.locale, options),
    startOperation: startOperationUtc.toLocaleString(config.locale, options),
    stopOperation: stopOperationUtc.toLocaleString(config.locale, options),
  }
}

/** neither duration nor delay must be zero, TODO type this somehow */
export type RollingPatternEnabledConfig = RollingPatternConfig

export type RollingPatternEnabledReport = RollingPatternReport & { enabled: 1 }
export type RollingPatternDisabledReport = RollingPatternReport & { enabled: 0 }
export const isRollingPatternConfig = (x: any): x is RollingPatternConfig => {
  const c = x as RollingPatternConfig
  return !!c && isNonNaNNumber(c.delay) && isNonNaNNumber(c.duration) && isNonNaNNumber(c.startTimeUtcSeconds)
}

/** a rolling pattern mode REQUEST where enabled doesn't exist because the fw doesn't support it, and either the delay or duration is zero as per fw behavior */
export const isRollingPatternDisabledConfig = (x: any): x is RollingPatternConfig => {
  return isRollingPatternConfig(x) && (x.delay === 0 || x.duration === 0)
}

export type RollingPatternRequest = RollingPatternConfig

export const isRollingPatternRequest = (x: any): x is RollingPatternRequest => !!x && !x.enabled && isRollingPatternConfig(x)

export type RollingPatternReport = RollingPatternConfig & { enabled: 0 | 1 }

/** a rolling pattern REPORT which include enabled flag  */
export const isRollingPatternReport = (x: any): x is RollingPatternReport => !!x && isNonNaNNumber(x.enabled) && isRollingPatternConfig(x)

/** a rolling pattern DISABLED REPORT  which include enabled flag  set to zero as per fw behavior */
export const isRollingPatternDisabledReport = (x: any): x is RollingPatternReport =>
  !!x && isNonNaNNumber(x.enabled) && x.enabled === 0 && isRollingPatternConfig(x)

type RollingProgrammeStartSlotToEntity = {
  duration: number
  delay: number
  startTimeUtcSeconds: number
  moistureThreshold: number
  enitites: ValidBusinessObjectList
}

export type RollingProgrammeToItemEdgeTypeAttributes = {
  windowIdx: number
}

export const isRollingProgrammeToItemEdgeType = (x: any): x is RollingProgrammeToItemEdgeInstance => {
  const c = x as RollingProgrammeToItemEdgeInstance
  const passes = !!c && isEdge(c) && !!c.edgeAttributes && isNonNaNNumber(c.edgeAttributes.windowIdx)

  debug && console.log(`INFO: isRollingProgrammeToItemEdgeType passes ${passes}`, x)
  return passes
}

export type RollingProgrammeToItemEdgeInstance = EdgeInstanceType<string, RollingProgrammeToItemEdgeTypeAttributes> & {
  edgeAttributes: RollingProgrammeToItemEdgeTypeAttributes
}

export type EntityRollingProgrammeConstructorProps = {
  /** the id of the programme which will be create if not present */
  id?: string
  /** the type id of the entity to be scheduled. As of 2024 12 one per scheduler, although this could be made mulitple with additional context queries */
  supportedType: string

  /** */
  enabled: boolean

  /** the absolute start time in seconds for this programme */
  startTimeUtcSeconds: number | null

  /** the number of start time windows to use. This combined with the duractionMins will determine the overall cycle of the programme, the delayMins is calculated based on windows * durationMins - durationMins.
   * This plus a future start time for each windown will yield consecutively operational programmes
   */
  windows: number | null

  /** a base duration in mins  */
  durationMins: number | null

  /** the duration in mins of the gap between windows, the need for this is apprent when there is only one window, which without this setting (and its fallback when zero) a single window programme is disabled because the
   * calculated delayMins is zero ( the device behavior then is to conclude the programme is disabled, as zero delayMins implies continous operation)
   */
  windowGapDurationMins: number

  /** the duration in mins of the gap between the end of the last window and the start of the next cycle */
  cycleGapDurationMins: number

  /** set the max number of entities per timeslot, if present. This is to allow enforcement of physical limitation eg Programme controls water system with limited pressure */
  maxNumberOfEntitiesPerTimeslot: number | null

  /** name of programme */
  name: string
  appliedTimestampsMs?: number[] | null

  /** Optional paramter to calculate */
  durationMinsEach?: (number | null)[]

  /** If populated, the next window that should go next. If null/undefined/0, start from the beginning */
  continueWindowCount?: number | null

  /** Valves ID that will control the entire programme */
  valves?: string[]

  /** Delay padding of the active period of the cycle */
  mvcDelay?: number | null
}

export interface IEntityRollingProgramme extends ValidBusinessObject {
  /** the type id of the entity to be scheduled. As of 2024 12 one per scheduler, although this could be made mulitple with additional context queries */
  supportedType: string
  /** whether this programme is enabled */
  enabled: boolean

  maxNumberOfEntitiesPerTimeslot: number | null
  /** name of programme */
  name: string

  windows: number | null

  /** duration of row */
  durationMins: number | null
  /** duration at each row if filled, otherwise, uses null */
  durationMinsEach?: (number | null)[]
  windowGapDurationMins: number
}
